import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquare, Check, CornerDownRight, AtSign } from 'lucide-react';
import apiService from '../../services/apiService';
import { FORECAST_USERS, getActiveForecastUser } from './forecastUsers';

/**
 * B4 — comment threads & mentions.
 *
 * Package-level (scopeType='package') or assumption-level (scopeType='assumption',
 * field-key anchored via assumptionKey). @mentions are parsed client-side for the
 * autocomplete UX but sent explicitly in mentions[] — the server validates the
 * explicit array, not the parsed body. Mentions emit 'mention' notifications (B1).
 *
 * Degrades gracefully: when the comment tables are absent the API returns an
 * empty thread list and the panel simply shows the composer.
 */

const fmtWhen = (value) => String(value || '').slice(0, 16).replace('T', ' ');

// Parse @Full Name tokens against the known actor list so multi-word names match.
const parseMentions = (body, actorNames) => {
  if (!body) return [];
  const found = new Set();
  actorNames.forEach((name) => {
    if (body.includes(`@${name}`)) found.add(name);
  });
  return [...found];
};

const ForecastCommentThreadPanel = ({
  submissionId,
  scopeType = 'package',
  assumptionKey = null,
  fieldLabel = null,
  title,
  compact = false
}) => {
  const activeUser = getActiveForecastUser();
  const actorNames = useMemo(() => FORECAST_USERS.map((u) => u.name), []);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState('');
  const [replyDrafts, setReplyDrafts] = useState({}); // threadId -> text
  const [busy, setBusy] = useState(false);
  const [suggest, setSuggest] = useState(null); // {target:'new'|threadId, matches:[]}
  const textareaRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { scopeType };
      if (assumptionKey) params.assumptionKey = assumptionKey;
      const result = await apiService.fetchForecastComments(submissionId, params);
      setThreads(result?.threads || []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (submissionId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId, scopeType, assumptionKey]);

  // Mention autocomplete: when the caret text ends with "@partial", suggest names.
  const onDraftChange = (value, target = 'new') => {
    if (target === 'new') setDraft(value);
    else setReplyDrafts((prev) => ({ ...prev, [target]: value }));
    const match = /@([A-Za-z .'\-]*)$/.exec(value);
    if (match) {
      const partial = match[1].toLowerCase();
      const matches = actorNames
        .filter((name) => name.toLowerCase().startsWith(partial) && name !== activeUser.name)
        .slice(0, 5);
      setSuggest(matches.length ? { target, matches } : null);
    } else {
      setSuggest(null);
    }
  };

  const applySuggestion = (name) => {
    if (!suggest) return;
    const { target } = suggest;
    const current = target === 'new' ? draft : (replyDrafts[target] || '');
    const next = current.replace(/@([A-Za-z .'\-]*)$/, `@${name} `);
    if (target === 'new') setDraft(next);
    else setReplyDrafts((prev) => ({ ...prev, [target]: next }));
    setSuggest(null);
  };

  const submitNew = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      await apiService.createForecastComment(submissionId, {
        actor: activeUser.name,
        scopeType,
        assumptionKey,
        fieldLabel,
        body,
        mentions: parseMentions(body, actorNames)
      });
      setDraft('');
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to post comment');
    } finally {
      setBusy(false);
    }
  };

  const submitReply = async (threadId) => {
    const body = (replyDrafts[threadId] || '').trim();
    if (!body) return;
    setBusy(true);
    try {
      await apiService.replyForecastComment(threadId, {
        actor: activeUser.name,
        body,
        mentions: parseMentions(body, actorNames)
      });
      setReplyDrafts((prev) => ({ ...prev, [threadId]: '' }));
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to reply');
    } finally {
      setBusy(false);
    }
  };

  const toggleResolve = async (thread) => {
    setBusy(true);
    try {
      await apiService.resolveForecastCommentThread(thread.id, {
        actor: activeUser.name,
        status: thread.status === 'resolved' ? 'open' : 'resolved'
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to update thread');
    } finally {
      setBusy(false);
    }
  };

  const renderSuggest = (target) => {
    if (!suggest || suggest.target !== target) return null;
    return (
      <div className="forecast-mention-suggest">
        {suggest.matches.map((name) => (
          <button type="button" key={name} onClick={() => applySuggestion(name)}>
            <AtSign size={11} /> {name}
          </button>
        ))}
      </div>
    );
  };

  const openCount = threads.filter((t) => t.status === 'open').length;

  return (
    <section className={`forecast-panel forecast-comment-panel${compact ? ' compact' : ''}`}>
      <div className="forecast-panel-head">
        <span className="forecast-section-code">
          <MessageSquare size={12} style={{ marginRight: 4, verticalAlign: '-2px' }} />
          {scopeType === 'assumption' ? 'RV_CM ● Assumption discussion' : 'RV_CM ● Package discussion'}
        </span>
        <h2>
          {title || (scopeType === 'assumption'
            ? `Comments — ${fieldLabel || assumptionKey}`
            : 'Reviewer & inputter comments')}
          {openCount > 0 && <span className="forecast-health-chip gold mini" style={{ marginLeft: 8 }}>{openCount} open</span>}
        </h2>
      </div>

      {error && <p className="forecast-submit-note" style={{ color: 'var(--ace-danger, #e5241d)' }}>{error}</p>}
      {loading && <p className="forecast-submit-note">Loading comments…</p>}

      {threads.length === 0 && !loading && (
        <p className="forecast-submit-note">No comments yet. Start a thread below — use @ to mention a teammate.</p>
      )}

      {threads.map((thread) => (
        <div key={thread.id} className={`forecast-comment-thread ${thread.status}`}>
          <div className="forecast-comment-thread-head">
            {thread.scopeType === 'assumption' && (
              <span className="forecast-driver-tag">{thread.fieldLabel || thread.assumptionKey}</span>
            )}
            <span className={`forecast-status-chip ${thread.status === 'resolved' ? 'moss' : 'gold'}`}>
              {thread.status === 'resolved' ? 'Resolved' : 'Open'}
            </span>
            <button type="button" className="forecast-home-action ghost" disabled={busy} onClick={() => toggleResolve(thread)}>
              <Check size={12} /> {thread.status === 'resolved' ? 'Reopen' : 'Resolve'}
            </button>
          </div>
          {(thread.comments || []).map((c) => (
            <div key={c.id} className="forecast-comment-item">
              <div className="forecast-comment-meta">
                <strong>{c.author}</strong>
                <span>{fmtWhen(c.createdAt)}</span>
              </div>
              <p className="forecast-comment-body">{c.body}</p>
              {(c.mentions || []).length > 0 && (
                <div className="forecast-comment-mentions">
                  {c.mentions.map((m) => (
                    <span key={m} className="forecast-health-chip neutral mini"><AtSign size={10} /> {m}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
          {thread.status === 'open' && (
            <div className="forecast-comment-reply">
              <textarea
                rows={1}
                placeholder="Reply… use @ to mention"
                value={replyDrafts[thread.id] || ''}
                onChange={(e) => onDraftChange(e.target.value, thread.id)}
              />
              {renderSuggest(thread.id)}
              <button type="button" className="forecast-home-action" disabled={busy} onClick={() => submitReply(thread.id)}>
                <CornerDownRight size={12} /> Reply
              </button>
            </div>
          )}
        </div>
      ))}

      <div className="forecast-comment-composer">
        <textarea
          ref={textareaRef}
          rows={compact ? 2 : 3}
          placeholder={scopeType === 'assumption'
            ? `Comment on ${fieldLabel || assumptionKey}… use @ to mention`
            : 'Start a comment… use @ to mention a teammate'}
          value={draft}
          onChange={(e) => onDraftChange(e.target.value, 'new')}
        />
        {renderSuggest('new')}
        <button type="button" className="forecast-primary-button compact" disabled={busy || !draft.trim()} onClick={submitNew}>
          Post comment
        </button>
      </div>
    </section>
  );
};

export default ForecastCommentThreadPanel;
